
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Mail, MapPin, Phone, Download } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const HeroBanner = () => {
  // ATS resume URL - updated to new Google Docs URL
  const atsResumeUrl = "https://docs.google.com/document/d/1W29a_P_ANL3mio4HBA6JowbaqX3iV4O5_sWWD7y0pgA/edit?usp=sharing";
  
  // Function to track resume downloads and show toast notification
  const handleResumeDownload = () => {
    window.open(atsResumeUrl, '_blank', 'noopener,noreferrer');
    console.log('Resume download: ATS-friendly resume');
    toast('Resume downloaded', {
      description: "You've successfully downloaded the ATS-friendly resume.",
    });
  };
  
  return (
    <section className="relative w-full pt-16 pb-10 md:pt-24 md:pb-16 px-6 bg-gradient-to-br from-sage-50 via-white to-warm-50 overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-sage-100 rounded-full -translate-y-1/2 translate-x-1/2 opacity-40"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-warm-100 rounded-full translate-y-1/3 -translate-x-1/3 opacity-40"></div>
      
      <div className="container max-w-5xl mx-auto flex flex-col md:flex-row gap-10 items-center relative z-10">
        <div className="md:w-1/3">
          <div className="relative">
            <div className="w-56 h-56 sm:w-64 sm:h-64 md:w-72 md:h-72 rounded-full overflow-hidden border-4 border-white shadow-lg">
              <img 
                src="/lovable-uploads/202d7088-df35-45e9-9b34-5cae17101b80.png" 
                alt="Payton Hunter, Psychology Graduate with AI Literacy" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-2 -right-2 bg-white p-1 rounded-full shadow-md">
              <div className="bg-sage-500 text-white p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"></path>
                  <path d="m9 12 2 2 4-4"></path>
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div className="md:w-2/3 text-center md:text-left">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-sage-800 mb-2">Payton Hunter</h1>
          <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
            <Badge variant="outline" className="bg-white/80 text-sage-700 border-sage-200 font-normal">
              Psychology Grad
            </Badge>
            <Badge variant="outline" className="bg-white/80 text-sage-700 border-sage-200 font-normal">
              AI-Curious Strategist
            </Badge>
            <Badge variant="outline" className="bg-white/80 text-sage-700 border-sage-200 font-normal">
              Adaptive Thinker
            </Badge>
            <Badge variant="outline" className="bg-white/80 text-sage-700 border-sage-200 font-normal">
              Creative Strategist
            </Badge>
          </div>
          
          <p className="font-serif text-lg md:text-xl italic text-sage-700 max-w-xl mb-6">
            "Helping people thrive through thoughtful systems, genuine connection, and steady care."
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start text-sm mb-5">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 hover:text-sage-700 transition-colors">
                    <MapPin size={16} className="text-sage-500" />
                    <span>Columbia, SC</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-white shadow-md">
                  <p>Based in Columbia, South Carolina</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 hover:text-sage-700 transition-colors">
                    <Mail size={16} className="text-sage-500" />
                    <a href="mailto:PaytonMHunter7@gmail.com" className="hover:underline">
                      PaytonMHunter7@gmail.com
                    </a>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-white shadow-md">
                  <p>Send me an email</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 hover:text-sage-700 transition-colors">
                    <Phone size={16} className="text-sage-500" />
                    <a href="tel:8037436199" className="hover:underline">
                      (803) 743-6199
                    </a>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-white shadow-md">
                  <p>Call or text me</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          
          {/* New Download Resume Button */}
          <div className="flex justify-center md:justify-start mb-2">
            <Button 
              className="bg-sage-600 hover:bg-sage-700 text-white flex items-center gap-2 transition-all duration-300 transform hover:scale-105"
              onClick={handleResumeDownload}
            >
              <Download size={18} />
              Download Résumé
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
